import type { SupportedFormData } from '../schemas/supportedFormSchema';
import type { SupportedFullInfoDtoOut } from '@/models/out/SupportedFullInfoDtoOut';
import type { DocumentDtoOut } from '@/models/out/DocumentDtoOut';
import { FileReaderHelper } from '@/helpers/fileReaderHelper';
/**
 * Transforms form data (with tracking IDs) to API DTO structure (without IDs in nested objects)
 * 
 * Why needed: Backend DTOs don't accept `id` fields in nested objects (spouse, children, 
 * bank accounts, classifications), but form needs them for UI tracking and future UPDATE support.
 * 
 * Also converts File objects to DocumentDtoOut with base64 content.
 * For contactClassifications documents:
 * - name: always uses the file name (file.name)
 * - description: uses requiredDocName for required documents, or customDescription for additional documents
 */
export async function mapFormDataToDto(data: SupportedFormData): Promise<SupportedFullInfoDtoOut> {
  // Convert shared children documents
  let childrenDocuments: DocumentDtoOut[] = [];
  if (data.childrenDocuments && data.childrenDocuments.length > 0) {
    const sharedDocs = await Promise.all(
      data.childrenDocuments.map(file => FileReaderHelper.fileToDocumentDtoOut(file))
    );
    childrenDocuments = sharedDocs.flat();
  }

  return {
    contact: {
      id: data.contact.id || undefined,
      israeliId: data.contact.israeliId,
      firstName: data.contact.firstName,
      lastName: data.contact.lastName,
      email: data.contact.email || undefined,
      phone: data.contact.phone || undefined,
      address: data.contact.address ? {
        ...data.contact.address,
        updatedAt: data.contact.address.updatedAt || new Date()
      } : undefined,
      birthDate: data.contact.birthDate,
    },
    spouse: data.spouse ? {
      firstName: data.spouse.firstName,
      israeliId: data.spouse.israeliId,
    } : null,
    childrenData: {
      children: data.children.map((child) => ({
        firstName: child.firstName,
        israeliId: child.israeliId,
        birthDate: child.birthDate,
        livesWithSupported: child.livesWithSupported,
      })),
      documents: childrenDocuments,
    },
    paymentInfos: await Promise.all(data.paymentInfos.map(async (account) => {
      let documents: DocumentDtoOut[] = [];
      const files = (account.documents || []).filter((doc): doc is File => doc instanceof File);
      if (files.length > 0) {
        const convertedDocs = await Promise.all(
          files.map(file => FileReaderHelper.fileToDocumentDtoOut(file))
        );
        documents = convertedDocs.flat();
      }
      return {
        type: account.type,
        bankNumber: account.bankNumber,
        branchNumber: account.branchNumber,
        accountNumber: account.accountNumber,
        accountOwnerName: account.accountOwnerName,
        documents,
      };
    })),
    contactClassifications: await Promise.all(data.contactClassifications.map(async (cc) => {
      let documents: DocumentDtoOut[] = [];
      
      // Process required documents
      const requiredDocs = (cc.requiredDocuments || [])
        .map(doc =>
          FileReaderHelper.fileToDocumentDtoOut(
            doc.file,
            doc.requiredDocName
          )
        );
      
      // Process additional documents
      const additionalDocs = (cc.additionalDocuments || [])
        .filter(doc => doc.file !== null)
        .map(doc =>
          FileReaderHelper.fileToDocumentDtoOut(
            doc.file!,
            doc.customDescription
          )
        );
      
      // Combine all documents
      const allDocs = await Promise.all([...requiredDocs, ...additionalDocs]);
      documents = allDocs.flat();
      
      return {
        classificationId: cc.classificationId,
        documents,
        comments: cc.comments || undefined,
      };
    })),
  };
}

/**
 * HAT-1464: the same form sent for a deleted supported being entered again, plus which of their
 * existing children and bank accounts stay. Children only come along while the family step is part
 * of the form - the same rule that clears the new children when it is not.
 */
export async function mapFormDataToReenterDto(
  data: SupportedFormData,
  { includeChildren }: { includeChildren: boolean },
): Promise<SupportedFullInfoDtoOut & { keptChildIds: number[]; keptPaymentInfoIds: number[] }> {
  return {
    ...(await mapFormDataToDto(data)),
    keptChildIds: includeChildren ? data.existingChildren.map((child) => child.id) : [],
    keptPaymentInfoIds: data.existingPaymentInfos.map((account) => account.id),
  };
}

